import React, { useEffect, useState } from "react";
import styles from "../shipments-table.module.css";
import { Table, TableColumn, TableState } from "@/components/Table/Table";
import { FBA_API_VERSION, FbaShipmentStatus, InboundBatch, ListShipmentsSort, TransportType, getShipmentStep } from "@boxem/core-api-schema";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import Skeleton from "@/components/Skeleton/Skeleton";
import Button from "@/components/forms/Button/Button";
import IconDownloadBox from "@/components/icons/IconDownloadBox";
import { cancelShipment, getBillOfLadingV2, getShippedShipments } from "@/api/fulfillment";
import { useErrorModalContext } from "@/contexts/ErrorModalContext";
import { useLocation } from "wouter";
import { useShipmentContext } from "@/contexts/ShipmentContext";
import ShipmentsZeroState from "@/components/ShipmentsZeroState/ShipmentsZeroState";
import { LocalStorageKeys, getLocalStorageItem } from "@/utils/localStorageUtils";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import { formatValue } from "@/utils/formatUtils";
import { formatISODate } from "@/utils/dateUtils";
import Flex from "@/components/Flex/Flex";
import Pagination from "@/components/Pagination/Pagination";
import ConfirmModal from "@/components/ConfirmModal/ConfirmModal";
import ShipmentStatusBadge from "@/components/ShipmentStatusBadge/ShipmentStatusBadge";
import SearchInput from "@/components/SearchInput";
import { API } from "@/lib/api";

interface ShippedShipmentsTableState {
  sortedColumnId: ListShipmentsSort | undefined;
  sortDirection: 'asc' | 'desc' | undefined;
  page: number;
  limit: number;
}

type ShippedShipmentTableRecord = {
  id: number;
  shipmentId: string;
  batchId: number | null;
  batchVersion: string | null,
  name: string;
  batchName?: string;
  type: TransportType;
  status?: FbaShipmentStatus;
  percentageReceived?: string;
  totalUnits: number;
  totalSkus: number;
  createdAt: Date;
  fromSync: boolean;
};

type ShippedShipmentsTableProps = {};

const ShippedShipmentsTable = ({}: ShippedShipmentsTableProps) => {
  const { setCurrentBatch, createShipment } = useShipmentContext();
  const { handleErrorResponseGenerically } = useErrorModalContext();
  const [location, setLocation] = useLocation();
  const [displayingShipments, setDisplayingShipments] = useState<ShippedShipmentTableRecord[]>([]);
  const [searchInputText, setSearchInputText] = useState<string>("");
  const [shipmentsLoading, setShipmentsLoading] = useState<boolean>(false);
  const [cancelLoading, setCancelLoading] = useState<number>(0);
  const [totalCount, setTotalCount] = useState(0);
  const [bolModalOpen, setBOLModalOpen] = useState<boolean>(false);
  const [bolLoading, setBolLoading] = useState<number>(0);
  const tableName = "shippedShipmentsTable";
  const [tableState, setTableState] = useState<ShippedShipmentsTableState>(() => {
    const tableProfile: ShippedShipmentsTableState = getLocalStorageItem(`${LocalStorageKeys.tableStatePrefix}${tableName}`);

    return {
      sortedColumnId: tableProfile?.sortedColumnId ?? undefined,
      sortDirection: tableProfile?.sortDirection ?? undefined,
      page: 1,
      limit: 10,
    };
  });

  const handleViewBOL = async (shipmentId: number) => {
    setBolLoading(shipmentId);
    const bol = await getBillOfLadingV2(shipmentId).finally(() => setBolLoading(0));

    if (!!bol?.url) {
      const a = document.createElement('a');
      a.href = bol?.url;
      a.target = "_blank";
      a.download = "bol.pdf";
      a.click();
    } else {
      setBOLModalOpen(true);
    }
  };

  const handleCancelShipment = async (shipment: ShippedShipmentTableRecord) => {
    setCancelLoading(shipment.id);
    await cancelShipment(shipment.id).finally(() => setCancelLoading(0));
  };

  const handleSortChange = async (columnId: ListShipmentsSort, direction: 'asc' | 'desc') => {
    setTableState({
      ...tableState,
      sortedColumnId: columnId,
      sortDirection: direction,
      page: 1,
    });
  };

  const handlePageSizeChange = async (newPageSize: number) => {
    setTableState({
      ...tableState,
      page: 1,
      limit: newPageSize,
    });
  };

  const handlePageChange = async (newPage: number) => {
    setTableState({
      ...tableState,
      page: newPage,
    });
  };

  const handleSearchChange = (value: string) => {
    setSearchInputText(value);
    if (value && tableState.page !== 1) {
      setTableState({
        ...tableState,
        page: 1,
      });
    }
  };

  const onClearSearch = () => {
    setSearchInputText("");
  };

  const columns: TableColumn<ShippedShipmentTableRecord>[] = [
    {
      title: 'Name',
      dataKey: 'name',
      id: 'name',
      sortable: true,
      width: '15rem',
      alignment: 'left',
      renderRowCell: (row) => (
        <div className={styles.shipmentItem}>
          <Tooltip
            title={row.getCellData('batchName') ?? row.getCellData('name')}
            placement='right'
          >
            {row.getCellData('name')}
          </Tooltip>
        </div>
      ),
      renderSkeleton: () => (
        <div className={styles.shipmentItem}>
          <Skeleton type="line-long" />
        </div>
      )
    },
    {
      title: 'Type',
      dataKey: 'type',
      id: 'type',
      sortable: true,
      width: '8rem',
      alignment: 'left',
      renderRowCell: (row) => {
        const shipmentStep = getShipmentStep(row.getCellData('status'));
        const showBOL = row.getCellData('type') === "LTL" && shipmentStep >= 4;

        return (
          <div className={styles.shipmentItem}>
            {row.getCellData('type')}
            {showBOL && (
              <div className={styles.bolButtonContainer}>
                <Button type="primary" onClick={() => handleViewBOL(row.getCellData('id'))}>
                  BOL
                  <div className={styles.downloadIcon}>
                    {bolLoading === row.getCellData('id') ? <LoadingSpinner className={styles.bolSpinner} size="1rem" /> : <IconDownloadBox />}
                  </div>
                </Button>
              </div>
            )}
          </div>
        );
      },
      renderSkeleton: () => (
        <div className={styles.shipmentItem}>
          <Skeleton type="line-short" />
        </div>
      )
    },
    {
      title: 'Status',
      dataKey: 'status',
      id: 'status',
      sortable: false,
      width: '8rem',
      alignment: 'left',
      renderRowCell: (row) => (
        <div className={styles.shipmentItem}>
          <ShipmentStatusBadge status={row.getCellData('status')} />
        </div>
      ),
      renderSkeleton: () => (
        <div className={styles.shipmentItem}>
          <Skeleton type="line-medium" />
        </div>
      )
    },
    {
      title: '% of units received',
      dataKey: 'percentOfUnits',
      id: 'percentOfUnits',
      sortable: false,
      width: '11rem',
      alignment: 'left',
      renderRowCell: (row) => {
        let percentageReceived = parseInt(row.getCellData('percentageReceived'));
        if (percentageReceived > 100) percentageReceived = 100; //Ensure that the max is always 100%.

        return (
          <div className={styles.shipmentItem}>
            <div className={styles.percentageReceivedContainer}>
              <div className={`${percentageReceived > 0 ? styles.percentageReceivedText : ""}`}>{percentageReceived}%</div>
              {percentageReceived > 0 && (
                <div
                  className={styles.percentageReceivedLoader}
                  style={{
                    width: `${percentageReceived}px`
                  }}
                />
              )}
            </div>
          </div>
        );
      },
      renderSkeleton: () => (
        <div className={styles.shipmentItem}>
          <Skeleton type="line-medium" />
        </div>
      )
    },
    {
      title: 'Units',
      dataKey: 'totalUnits',
      id: 'totalUnits',
      sortable: true,
      width: '5rem',
      alignment: 'left',
      renderRowCell: (row) => formatValue(row.getCellData('totalUnits'), 'number'),
      renderSkeleton: () => (
        <div className={styles.shipmentItem}>
          <Skeleton type="line-short" />
        </div>
      )
    },
    {
      title: 'SKUs',
      dataKey: 'totalSkus',
      id: 'totalSkus',
      sortable: true,
      width: '5rem',
      alignment: 'left',
      renderRowCell: (row) => formatValue(row.getCellData('totalSkus'), 'number'),
      renderSkeleton: () => (
        <div className={styles.shipmentItem}>
          <Skeleton type="line-short" />
        </div>
      )
    },
    {
      title: 'Created At',
      dataKey: 'createdAt',
      id: 'createdAt',
      sortable: true,
      width: '11.5rem',
      alignment: 'left',
      renderRowCell: (row) => (
        <div className={styles.shipmentItem}>
          {!!row.getCellData('fromSync') ? (
            <div className={styles.externalBadge}>
              External Shipment
            </div>
          ) : (
            <>
              {formatISODate(row.getCellData('createdAt'))}
            </>
          )}
        </div>
      ),
      renderSkeleton: () => (
        <div className={styles.shipmentItem}>
          <Skeleton type="line-short" />
        </div>
      )
    },
    {
      title: '',
      dataKey: undefined,
      id: 'actions',
      sortable: false,
      pinned: 'right',
      alignment: 'right',
      width: '10rem',
      renderRowCell: (row) => {
        const currentDate = new Date();
        const pastDate = new Date(row.getCellData('createdAt').getTime() + (24 * 60 * 60 * 1000));
        const olderThan24Hours = currentDate >= pastDate;
        const fromOutsideBoxem = row.getCellData('fromSync');
        /* @NOTE(michaelp): Comment from Nick in Slack
          In v1, when your batch got split into shipments, each shipment was considered independently to each other. You could cancel each shipment on its own, and this wouldn't affect the others.
          This isn't the case in v2 anymore. You either cancel all shipments within a batch, or none of them.
          To that end, what I thought we did was make the button say 'Resume', and now if you want to cancel you go Resume -> Cancel in top-right.
        */
        const isV1 = row.getCellData('batchVersion') === '1';

        const record: ShippedShipmentTableRecord = row.rowData;
        return (
          <div className={styles.shipmentItem}>
            <Flex gap={4} align="center">
                <a href={(record.fromSync || !record.batchId || record.batchVersion === '1')
                          ? `/shipments/external/${record.id}?prev=shipped`
                          : `/fba/shipments/${record.batchId}?record=${record.shipmentId}&return=${encodeURIComponent(location)}`}
                >
                  <Button type="secondary">View</Button>
                </a>
              
              {(!olderThan24Hours && !fromOutsideBoxem && isV1) && (
                <Button type="secondary" onClick={() => handleCancelShipment(row.rowData)}>
                  {cancelLoading === row.getCellData('id') ? <LoadingSpinner size="1.5rem" /> : (
                    "Cancel"
                  )}
                </Button>
              )}
            </Flex>
          </div>
        );
      },
    },
  ];

  const handleGetShippedShipments = async () => {
    const ascending: boolean = tableState.sortDirection == "asc" ? true : false;
    return await getShippedShipments(tableState.page - 1, tableState.limit, searchInputText, tableState.sortedColumnId, ascending)
      .then(async res => {
        setTotalCount(res.count);
        const newShippedShipments: ShippedShipmentTableRecord[] = [];

        // @NOTE(michaelp): Somewhat duplicated code in ClosedShipments, search codebase for (NEMOZOID)
        let batchPromises = [];
        for (let i = 0; i < res.shipments.length; i++) {
          const shipment = res.shipments[i];
          if (shipment.batchId) {
            batchPromises[i] = API.get<InboundBatch>(`${FBA_API_VERSION}/fba/batches/${shipment.batchId}`);
          } else batchPromises[i] = null;
        }
        const fetchedBatches: (InboundBatch|null)[] = await Promise.all(batchPromises);
        for (let i = 0; i < res.shipments.length; i++) {
          const shipment = res.shipments[i];
          const batch: InboundBatch|null = fetchedBatches[i];

          let totalUnits = 0;
          let totalUnitsReceived = 0;
          for (const item of shipment.items) {
            totalUnits += item.quantity;
            totalUnitsReceived += item.quantityReceived;
          }

          newShippedShipments.push({
            id: shipment.id,
            shipmentId: shipment.shipmentId,
            batchId: shipment.batchId,
            name: shipment.shipmentName,
            batchName: batch?.name ?? null,
            batchVersion: batch?.version ?? "2",
            type: batch?.shipmentType ?? shipment.transportType,
            status: shipment.status,
            totalSkus: shipment.items.length,
            totalUnits,
            percentageReceived: String(Math.round((totalUnitsReceived / totalUnits) * 100) || 0),
            createdAt: shipment.createdAt,
            fromSync: shipment.fromSync,
          })
        }

        setDisplayingShipments(newShippedShipments);
      });
  };

  useEffect(() => {
    setShipmentsLoading(true);
    handleGetShippedShipments().catch(err => handleErrorResponseGenerically(err)).finally(() => setShipmentsLoading(false));
  }, [
    searchInputText,
    tableState.page,
    tableState.limit,
    tableState.sortDirection,
    tableState.sortedColumnId,
  ]);

  return (
    <>
      <Table
        data={displayingShipments}
        columns={columns}
        tableName={tableName}
        className={styles.table}
        isLoading={shipmentsLoading}
        skeletonRows={tableState.limit}
        sortDirection={tableState.sortDirection}
        sortedColumnId={tableState.sortedColumnId}
        onSort={handleSortChange}
        showSort
        header={
          <>
            <div className={styles.searchInputContainer}>
              <SearchInput
                searchTerm={searchInputText}
                onDebounceChange={handleSearchChange}
                placeholder="Search by shipment name or ID"
                className={styles.searchInput}
              />
            </div>
          </>
        }
        zeroState={
          (!shipmentsLoading && displayingShipments?.length === 0 && searchInputText === "") ? (
            <ShipmentsZeroState
              type="default"
              onCreateShipment={createShipment}
            />
          ) : undefined
        }
        noResultsState={
          (!shipmentsLoading && displayingShipments?.length === 0 && searchInputText !== "") ? (
            <ShipmentsZeroState
              type="search"
              onClearSearch={onClearSearch}
            />
          ) : undefined
        }
        footer={
          <Pagination
            total={totalCount || 0}
            pageSize={tableState.limit}
            onChangePageSize={handlePageSizeChange}
            page={tableState.page}
            onChangePage={handlePageChange}
            pageSizeOptions={[10, 20, 50, 100]}
          />
        }
      />

      <ConfirmModal
        title={"BOL Not Ready"}
        body={<p style={{margin:"20px", padding:"20px"}}>The bill of lading for this shipment is not ready yet - the BOL should be generated 1 day before the day of pick up.</p>} 
        open={bolModalOpen}
        onClose={(_: boolean): void => setBOLModalOpen(false)}
      />
    </>
  );
};

export default ShippedShipmentsTable;